// ABOUTME: React context tracking the current user's active cart and exposing cart actions.
// ABOUTME: Provides item count, drawer open state, and add/discard/convert/edit operations.

import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { AddToCartOptions, ConvertCartPayload, useCart } from '../../hooks/useCart';
import { useIsCartEnabled } from '../../hooks/useCpqSettings';
import { OfferDetail } from '../../hooks/useOffer';

interface CartContextValue {
    enabled: boolean;
    cart: OfferDetail | null;
    itemCount: number;
    loading: boolean;
    refresh: () => Promise<void>;
    addToCart: (productId: string, quantity?: number, options?: AddToCartOptions) => Promise<void>;
    discard: () => Promise<void>;
    convert: (payload: ConvertCartPayload) => Promise<{ offerId: string; offerNumber: string }>;
    updateItem: (lineItemId: string, payload: { quantity?: number }) => Promise<void>;
    removeItem: (lineItemId: string) => Promise<void>;
    isDrawerOpen: boolean;
    openDrawer: () => void;
    /** Identifies the current cart: bumped each time the cart is discarded or converted. */
    getCartGeneration: () => number;
    /**
     * Opens the drawer after the first add to the current cart only; later adds leave it as it is.
     * When `generation` (read via getCartGeneration before the add started) no longer matches the
     * current cart, the add belonged to a discarded or converted cart and is ignored.
     */
    openDrawerOnFirstAdd: (generation?: number) => void;
    closeDrawer: () => void;
}

const CartContext = createContext<CartContextValue | null>(null);

/**
 * Hook to consume the cart context. Returns null when used outside a CartProvider.
 */
export function useCartContext(): CartContextValue | null {
    return useContext(CartContext);
}

export function CartProvider({ children }: { children: ReactNode }) {
    const enabled = useIsCartEnabled();
    const {
        loading,
        getActiveCart,
        addToCart: addToCartApi,
        discardCart,
        convertCart,
        updateCartItem,
        removeCartItem,
    } = useCart();

    const [cart, setCart] = useState<OfferDetail | null>(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    // Whether an add already auto-opened the drawer. Once it has, a drawer the user closed stays
    // closed on later adds (they only get the toast). Reset when the cart is discarded or converted.
    const drawerAutoOpenedRef = useRef(false);
    // Bumped when the cart is discarded or converted, so an add that started on the previous cart
    // and resolves afterwards cannot use up the first-add auto-open of the new one.
    const cartGenerationRef = useRef(0);

    const refresh = useCallback(async () => {
        if (!enabled) {
            setCart(null);
            return;
        }
        try {
            const result = await getActiveCart();
            setCart(result ?? null);
        } catch {
            setCart(null);
        }
    }, [enabled, getActiveCart]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const cartId = cart?.offer?.id as string | undefined;

    const addToCart = useCallback(
        async (productId: string, quantity = 1, options?: AddToCartOptions) => {
            await addToCartApi(productId, quantity, options);
            await refresh();
        },
        [addToCartApi, refresh],
    );

    const discard = useCallback(async () => {
        await discardCart();
        drawerAutoOpenedRef.current = false;
        cartGenerationRef.current += 1;
        await refresh();
    }, [discardCart, refresh]);

    const convert = useCallback(
        async (payload: ConvertCartPayload) => {
            const result = await convertCart(payload);
            drawerAutoOpenedRef.current = false;
            cartGenerationRef.current += 1;
            await refresh();
            return result;
        },
        [convertCart, refresh],
    );

    const updateItem = useCallback(
        async (lineItemId: string, payload: { quantity?: number }) => {
            if (!cartId) return;
            await updateCartItem(cartId, lineItemId, payload);
            await refresh();
        },
        [cartId, updateCartItem, refresh],
    );

    const removeItem = useCallback(
        async (lineItemId: string) => {
            if (!cartId) return;
            await removeCartItem(cartId, lineItemId);
            await refresh();
        },
        [cartId, removeCartItem, refresh],
    );

    const openDrawer = useCallback(() => setIsDrawerOpen(true), []);
    const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);
    const getCartGeneration = useCallback(() => cartGenerationRef.current, []);
    const openDrawerOnFirstAdd = useCallback((generation?: number) => {
        if (generation !== undefined && generation !== cartGenerationRef.current) return;
        if (drawerAutoOpenedRef.current) return;
        drawerAutoOpenedRef.current = true;
        setIsDrawerOpen(true);
    }, []);

    const itemCount = cart?.lineItems?.length ?? 0;

    const value = useMemo<CartContextValue>(
        () => ({
            enabled,
            cart,
            itemCount,
            loading,
            refresh,
            addToCart,
            discard,
            convert,
            updateItem,
            removeItem,
            isDrawerOpen,
            openDrawer,
            getCartGeneration,
            openDrawerOnFirstAdd,
            closeDrawer,
        }),
        [
            enabled,
            cart,
            itemCount,
            loading,
            refresh,
            addToCart,
            discard,
            convert,
            updateItem,
            removeItem,
            isDrawerOpen,
            openDrawer,
            getCartGeneration,
            openDrawerOnFirstAdd,
            closeDrawer,
        ],
    );

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
